import type { Figure } from "../api";
import { Money } from "./Money";
import type { Tone } from "./tone";

/**
 * The whole set as one bar, and the filter for the rows behind it.
 *
 * Five separate tiles gave five numbers and no sense of proportion — a
 * reader could not see at a glance that most of the list was already
 * settled. Drata's posture bar puts the distribution in one row with the
 * counts beneath, which is the shape of the fact.
 *
 * The segments differ by weight rather than hue, so the alarm colour is
 * still spent on exactly one thing.
 */
interface Band {
  tone: Tone;
  n: number;
  label: string;
}

export function Distribution({ bands, total, winnable, lost, filter, onFilter }: {
  bands: Band[];
  total: number;
  winnable: Figure;
  lost: Figure;
  filter: Tone | null;
  onFilter: (f: Tone | null) => void;
}) {
  const shown = bands.filter((b) => b.n > 0);

  return (
    <div className="dist2">
      <div className="dist2__bar" role="img"
        aria-label={shown.map((b) => `${b.n} ${b.label}`).join(", ")}>
        {shown.map((b) => (
          <button key={b.tone} type="button"
            className={`dist2__seg dist2__seg--${b.tone}${filter === b.tone ? " is-on" : ""}`}
            style={{ flexGrow: b.n }}
            aria-pressed={filter === b.tone}
            title={filter === b.tone ? "Show all" : `Show only these ${b.n}`}
            onClick={() => onFilter(filter === b.tone ? null : b.tone)} />
        ))}
      </div>

      <div className="dist2__keys">
        {shown.map((b) => (
          <button key={b.tone} type="button"
            className={`dist2__key dist2__key--${b.tone}${filter === b.tone ? " is-on" : ""}`}
            aria-pressed={filter === b.tone}
            onClick={() => onFilter(filter === b.tone ? null : b.tone)}>
            {/* A count without a denominator makes a reader do arithmetic
                to find out whether four is most of the list or a corner
                of it. Borrowed from Drata's "out of 17 total". */}
            <span className="dist2__n">{b.n}<span className="dist2__of"> of {total}</span></span>
            <span className="dist2__label">{b.label}</span>
          </button>
        ))}
        <div className="dist2__money">
          <span className="dist2__fig"><Money value={winnable} /></span>
          <span className="dist2__label">still winnable</span>
        </div>
        <div className="dist2__money dist2__money--spent">
          <span className="dist2__fig"><Money value={lost} /></span>
          <span className="dist2__label">already lost</span>
        </div>
      </div>
    </div>
  );
}
